import { z } from 'zod'

/**
 * 共有URL（ShareLink）リソースの Zod スキーマ。
 *
 * 関連: docs/system-requirements.md §4.5 / packages/db/prisma/schema.prisma の ShareLink model
 */

export const SharePermissionSchema = z.enum(['VIEW', 'DOWNLOAD', 'UPLOAD'])
export type SharePermission = z.infer<typeof SharePermissionSchema>

export const ShareLinkSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  token: z.string(),
  permission: SharePermissionSchema,
  emailRequired: z.boolean(),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  fileIds: z.array(z.string().uuid()),
})
export type ShareLink = z.infer<typeof ShareLinkSchema>

// ----- Path / Query parameters -----

export const ShareLinkIdParamsSchema = z.object({
  id: z.string().uuid(),
})

export const ShareTokenParamsSchema = z.object({
  token: z.string().min(1).max(64),
})

export const ShareTokenFileParamsSchema = z.object({
  token: z.string().min(1).max(64),
  fileId: z.string().uuid(),
})

// ----- オーナー: 共有URL作成 -----

export const CreateShareLinkInputSchema = z
  .object({
    fileIds: z.array(z.string().uuid()).default([]),
    permission: SharePermissionSchema,
    expiresAt: z.string().datetime(),
    emailRequired: z.boolean().default(false),
  })
  .refine((v) => v.permission === 'UPLOAD' || v.fileIds.length > 0, {
    message: 'VIEW / DOWNLOAD 権限ではファイル選択が必須です',
    path: ['fileIds'],
  })
export type CreateShareLinkInput = z.infer<typeof CreateShareLinkInputSchema>

export const ListShareLinksResultSchema = z.object({
  items: z.array(ShareLinkSchema),
})
export type ListShareLinksResult = z.infer<typeof ListShareLinksResultSchema>

// ----- ゲスト: メタ取得（GET /share/:token） -----

/** 認証不要の最小情報。ファイルメタ自体はアクセス認証後に取得する */
export const ShareLinkPublicMetaSchema = z.object({
  permission: SharePermissionSchema,
  emailRequired: z.boolean(),
  expiresAt: z.string().datetime(),
  revoked: z.boolean(),
  fileCount: z.number().int().nonnegative(),
  /** プロジェクト名のみ（ヒント用） */
  projectName: z.string(),
})
export type ShareLinkPublicMeta = z.infer<typeof ShareLinkPublicMetaSchema>

// ----- ゲスト: アクセス認証（POST /share/:token/access） -----

export const ShareLinkAccessInputSchema = z.object({
  email: z.string().email().optional(),
  /** クライアント側で発行した FingerprintJS の visitor id（暫定実装は文字列のみ受領） */
  visitorId: z.string().min(1).max(128).optional(),
})
export type ShareLinkAccessInput = z.infer<typeof ShareLinkAccessInputSchema>

export const ShareLinkAccessOutputSchema = z.object({
  guestToken: z.string(),
  expiresAt: z.string().datetime(),
  permission: SharePermissionSchema,
  fileIds: z.array(z.string().uuid()),
  projectName: z.string(),
})
export type ShareLinkAccessOutput = z.infer<typeof ShareLinkAccessOutputSchema>

// ----- ゲスト: ファイル一覧（認証後） -----

const GuestFileSchema = z.object({
  id: z.string().uuid(),
  originalName: z.string(),
  sizeBytes: z.string(),
  durationSec: z.number().int().nullable(),
  mimeType: z.string(),
  streamUid: z.string().nullable(),
  streamStatus: z.enum(['PENDING', 'QUEUED', 'READY', 'ERROR']).nullable(),
  /** 動画サムネイル（CF Stream 公開URL）。READY 時のみ。画像は別途 preview-url を引く */
  thumbnailUrl: z.string().url().nullable(),
})
export type GuestFile = z.infer<typeof GuestFileSchema>

export const GuestFilesResultSchema = z.object({
  items: z.array(GuestFileSchema),
})
export type GuestFilesResult = z.infer<typeof GuestFilesResultSchema>

// ----- ゲスト: アップロード -----

export const GuestRequestUploadUrlInputSchema = z.object({
  originalName: z.string().min(1).max(500),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024 * 1024 * 1024),
  mimeType: z.string().min(1).max(100),
})
export type GuestRequestUploadUrlInput = z.infer<typeof GuestRequestUploadUrlInputSchema>
